/* ==================================================================
   GB — Sticky Add to cart. Mobile only.

   Loaded by snippets/buy-buttons.liquid ONLY, and only when the Buy
   buttons block has the setting on. Pairs with
   component-sticky-atc.css, which carries the reasoning for why this
   is position: sticky and not position: fixed, and what the rail is.

   This file measures ONE thing: how tall the rail has to be so the
   button stays pinned to the bottom of <main> and not a pixel
   further. There is no CSS expression for "reach the bottom of that
   ancestor", and the number matters twice over. Too short and the
   button lets go early. Too long and the rail's box overflows past the
   content below it and the document grows a scrollbar to match,
   measured at 100,355px on a page that should be 9,991px.

   It also measures the header's bottom edge and how far a transformed
   ancestor drags the pinned button. Both are written out as custom
   properties for the stylesheet to use.

   No scroll listener: CSS sticky does the pinning and the release on
   its own. Two observers, both idle until something actually changes.

   If any of this fails the element never gets data-ready, the CSS
   applies nothing, and the page is byte for byte the stock one.
   ================================================================== */

/* Feature gate. If any one of these is missing the element is never
   defined, <sticky-add-to-cart> stays an unknown inline-block element
   wrapping the button, data-ready is never set, and the stylesheet
   therefore applies no height, no pull and no sticky. The page is the
   stock one.

   Every API the element touches is listed, including the ones that in
   practice shipped years before ResizeObserver and so cannot really be
   missing on their own. Naming them all is one line each and makes the
   guarantee above true by construction, rather than true because of an
   argument about browser history that nobody will re-check later.
   IntersectionObserver belongs here rather than being handled softly:
   without it the button would pin with no backdrop behind it, and a
   half-drawn feature is worse than none. */
if (
  window.customElements &&
  window.ResizeObserver &&
  window.IntersectionObserver &&
  window.matchMedia &&
  window.requestAnimationFrame &&
  !customElements.get('sticky-add-to-cart')
) {
  customElements.define(
    'sticky-add-to-cart',
    class StickyAddToCart extends HTMLElement {
      connectedCallback() {
        this.rail = this.querySelector('.gb-satc__rail');
        this.dock = this.querySelector('.gb-satc__dock');
        this.column = this.closest('.product__info-container');
        if (!this.rail || !this.dock || !this.column) return;

        /* The release point. The button stays pinned all the way down to
           the bottom of <main>, so it is still there over Related products
           and Recently browsed, and lets go just before the footer. A
           footer is not a buying context and nobody wants a button parked
           over it, so that is where it stops rather than running to the
           very end of the document.

           It has to be a real element that ends INSIDE the document. The
           rail is stretched to reach it, and a rail whose box overshoots
           the content below it grows the page: measured at 100,355px on a
           page that should be 9,991px. <main> always ends inside.

           Falls back to the product details column if a theme ever renders
           this outside a <main>. */
        this.anchor = this.closest('main') || this.column;

        // Only ever one pinned button per page. This snippet is rendered by
        // BOTH main-product and featured-product, so a page carrying a
        // Featured product section as well as the product itself could put
        // two of these in the document. Two buttons docking at the same
        // line would sit on top of each other. First one in the document
        // wins, the rest stay inert, and the setting can safely be added to
        // featured-product later without revisiting this.
        if (document.querySelector('sticky-add-to-cart') !== this) return;

        // Same breakpoint as the stylesheet and as main-product's own
        // one-column switch.
        this.mobile = window.matchMedia('(max-width: 749px)');

        // Covers every reason the numbers could change: a resize, a
        // rotation, crossing 750px, the trust badge image loading late,
        // the description reflowing, inventory text changing on a
        // variant switch. All of them alter the column's box, so one
        // observer catches the lot. No window listeners needed.
        /* One frame of coalescing. ResizeObserver delivers after layout and
           before paint, so a measurement taken here still lands in the frame
           the shopper sees: the gap under the header was checked against
           screenshots across the whole 300ms logo-shrink animation and is 11px
           on every painted frame. Deferring by a frame costs nothing visible
           and keeps a burst of resizes to a single re-measure. */
        this.observer = new ResizeObserver(() => {
          cancelAnimationFrame(this.frame);
          this.frame = requestAnimationFrame(() => this.measure());
        });
        this.observer.observe(this.column);
        this.observer.observe(this.anchor);
        this.observer.observe(this.dock);

        /* The header is observed too, and this is not optional.
           This header's sticky mode is "reduce logo size": the moment the
           shopper scrolls past it Dawn adds .scrolled-past-header and
           header.liquid drops the logo to 75% width, with no media query,
           so the header gets SHORTER on mobile at almost exactly the
           moment the button pins. The button parks a fixed 11px under the
           header's bottom edge, so a header height measured before that
           and never re-read leaves the gap visibly wrong, and different
           depending on how the shopper arrived at that scroll position.

           Observed rather than recomputed on scroll: it re-measures when
           the header's box actually changes, whatever the cause — the
           logo shrinking, an announcement bar being dismissed, a menu
           opening, or a different sticky mode being chosen in the editor
           later. Nothing here knows or cares which. */
        this.header = document.querySelector('sticky-header');
        if (this.header) this.observer.observe(this.header);

        this.measure();
      }

      disconnectedCallback() {
        cancelAnimationFrame(this.frame);
        this.observer?.disconnect();
        this.stuck?.disconnect();
      }

      /* Everything measured runs inside this. A browser quirk, a computed
         style that will not parse, a node pulled out from under us
         mid-measure: none of it may surface as an uncaught error on a
         product page, and none of it may leave the element half applied
         with a rail height but no pull, which would move the page.
         reset() puts it back to the stock page, which is the same state
         it would be in if this file had never loaded. */
      measure() {
        try {
          this.update();
        } catch (error) {
          this.reset();
        }
      }

      // The stock state. Also what runs above 749px, where this element
      // must do nothing at all: with data-ready cleared the stylesheet
      // applies no height, no pull and no sticky.
      reset() {
        this.removeAttribute('data-ready');
        this.classList.remove('is-stuck');
        this.stuck?.disconnect();
        this.stuck = null;
        this.line = null;
      }

      update() {
        if (!this.isConnected) return;

        if (!this.mobile.matches) {
          this.reset();
          return;
        }

        const styles = getComputedStyle(this.dock);
        const height = this.dock.getBoundingClientRect().height;
        const marginBottom = parseFloat(styles.marginBottom);

        /* A measurement that cannot be trusted is worse than no feature at
           all. --gb-satc-pull is what cancels the rail's height back out of
           the flow, so if dockOuter is wrong by n pixels then every element
           below the button moves by n and the page changes height. Silent,
           and exactly the regression this must never cause. Falling back to
           the stock page is the only safe answer. */
        if (!(height > 0) || !Number.isFinite(marginBottom)) {
          this.reset();
          return;
        }

        const dockOuter = Math.round(height + marginBottom);

        // How far the rail has to reach: from where it sits to the
        // bottom of the anchor. The rail's own top does not move when its
        // height changes, because --gb-satc-pull cancels that height
        // again, so this is stable on re-runs rather than compounding.
        const rail = Math.max(
          dockOuter,
          Math.round(this.anchor.getBoundingClientRect().bottom - this.rail.getBoundingClientRect().top)
        );

        this.style.setProperty('--gb-satc-rail', `${rail}px`);
        this.style.setProperty('--gb-satc-pull', `${dockOuter - rail}px`);
        this.style.setProperty('--gb-satc-shift', `${this.ancestorShift()}px`);
        this.style.setProperty('--gb-satc-header', `${this.headerBottom()}px`);
        this.setAttribute('data-ready', '');

        // The pin line is resolved by CSS, so read it back rather than
        // recomputing it here. One source of truth. getComputedStyle
        // returns the compensated value, so add the shift back to get
        // the line the button actually lands on in the viewport.
        this.watch(Math.round((parseFloat(styles.top) || 0) + this.ancestorShift()));
      }

      /* The bottom edge of the sticky header, which is where the button
         parks under. 0 when the header is not sticky, because then there
         is nothing overhead to clear.

         Measured from the real element rather than derived from
         --header-height and --header-area-margin. Two reasons. It is
         correct whatever the logo size, the announcement bar or the
         header margin are doing, without depending on those variables
         existing. And the alternative needed body:has(sticky-header) to
         tell the sticky and non-sticky cases apart, which would have made
         the whole feature depend on :has(); where that is missing the
         rule is dropped and the button pins ABOVE the viewport top,
         behind the header. Measuring removes that failure mode. */
      headerBottom() {
        const header = this.header;
        if (!header || !header.isConnected) return 0;

        const section = header.closest('.shopify-section') || header.parentElement;
        if (!section) return 0;

        const styles = getComputedStyle(section);
        if (styles.position !== 'sticky' && styles.position !== 'fixed') return 0;

        /* Not rounded. The button parks a fixed 11px under this value, so a
           rounding error here would land straight in that gap. In practice
           the browser's own device-pixel snapping absorbs it and the painted
           gap is 11px either way, checked against screenshots at 360, 414,
           745 and 749px — so this is not load-bearing, it is just the
           accurate number, and one call shorter than rounding it. */
        return header.getBoundingClientRect().height + (parseFloat(styles.top) || 0);
      }

      /* How far a transformed ancestor drags the pinned button.

         position: sticky pins against the element's LAYOUT position,
         and a transform on an ancestor then moves the whole painted
         subtree afterwards. The two do not cancel out.

         That is not hypothetical here. .product__info-wrapper carries
         Dawn's scroll-reveal classes, and on the product page it sits
         permanently at matrix(1, 0, 0, 1, 0, 20) because the reveal
         animation never actually plays on it. So the dock pinned at
         143px was painting at 163px: 20px too low, and the whole
         product column is 20px lower than its own layout says.

         Measuring the ancestors and subtracting the total means the
         button lands where the CSS says it should, without touching
         the reveal animation or moving anything else on the page. If
         Dawn ever fixes that animation the measurement simply returns
         0 and nothing here changes. */
      ancestorShift() {
        let shift = 0;
        for (let el = this.parentElement; el && el !== document.documentElement; el = el.parentElement) {
          const t = getComputedStyle(el).transform;
          if (t && t !== 'none') {
            try {
              shift += new DOMMatrixReadOnly(t).m42;
            } catch (e) {
              /* unparseable transform, treat as no shift */
            }
          }
        }
        return Math.round(shift);
      }

      /* Tells the stylesheet when the button is actually pinned, so the
         backdrop band is painted then and only then. At rest nothing
         extra is drawn, whatever colour scheme the section is on.

         Watch the dock with the viewport's top edge pulled down past
         the pin line. Resting, the dock is fully inside that box and
         the ratio is 1. The moment sticky lifts it to the line it
         clips, the ratio drops, and that is the pinned state. */
      watch(line) {
        if (this.line === line && this.stuck) return;
        this.stuck?.disconnect();
        this.line = line;

        this.stuck = new IntersectionObserver(
          ([entry]) => this.classList.toggle('is-stuck', entry.intersectionRatio < 1),
          { threshold: [1], rootMargin: `-${line + 1}px 0px 0px 0px` }
        );
        this.stuck.observe(this.dock);
      }
    }
  );
}
